import type {
  ActivityStatusExpiryKind,
  ManualActivityPresetId,
  ProviderActivityStatus,
} from '@shared/provider-activity/types'

export type ActivityPreset = {
  id: ManualActivityPresetId
  emoji: string
  label: string
  defaultDurationMinutes: number | null
}

/**
 * Manual presets providers can pick today. AHP will supply in-session /
 * travelling later.
 *
 * NOTE: the `id` values are the stable storage keys persisted in
 * `settingsJson.activityStatus.presetId`. They are intentionally NOT renamed
 * when labels change (e.g. `vacation` → "Out of office", `out-sick` →
 * "Unwell") so providers already set to the old label keep resolving — the
 * label/emoji are re-derived from the preset on read (see
 * {@link getProviderActivityStatus}).
 */
export const MANUAL_ACTIVITY_PRESETS: ActivityPreset[] = [
  { id: 'in-meeting', emoji: '🗓️', label: 'In a meeting', defaultDurationMinutes: 60 },
  { id: 'commuting', emoji: '🚌', label: 'Commuting', defaultDurationMinutes: 30 },
  { id: 'out-sick', emoji: '🤒', label: 'Unwell', defaultDurationMinutes: null },
  { id: 'vacation', emoji: '🌴', label: 'Out of office', defaultDurationMinutes: null },
  { id: 'working-remotely', emoji: '🏠', label: 'Working remotely', defaultDurationMinutes: null },
  { id: 'focus-time', emoji: '🎯', label: 'Focus time', defaultDurationMinutes: 120 },
]

const MANUAL_PRESET_BY_ID = new Map<ManualActivityPresetId, ActivityPreset>(
  MANUAL_ACTIVITY_PRESETS.map((preset) => [preset.id, preset]),
)

/** Preset backing the "Out of office" status (kept under the legacy `vacation` key). */
export const OUT_OF_OFFICE_PRESET = MANUAL_PRESET_BY_ID.get('vacation')!

/** Preset backing the "Unwell" status (kept under the legacy `out-sick` key). */
export const UNWELL_PRESET = MANUAL_PRESET_BY_ID.get('out-sick')!

/**
 * Choose the activity preset that should back a posted Leave update.
 *
 * Sick leave maps to "Unwell"; every other leave type maps to "Out of office".
 * Matching is case-insensitive and tolerant of the trailing " leave" wording so
 * it keeps working if the leave-tag copy is tweaked.
 */
export function leavePresetForType(leaveType: string | null | undefined): ActivityPreset {
  const normalized = (leaveType ?? '').trim().toLowerCase()
  if (normalized === 'sick leave' || normalized === 'sick') return UNWELL_PRESET
  return OUT_OF_OFFICE_PRESET
}

export const ACTIVITY_DURATION_OPTIONS: { label: string; minutes: number | null }[] = [
  { label: '30 minutes', minutes: 30 },
  { label: '1 hour', minutes: 60 },
  { label: '2 hours', minutes: 120 },
  { label: '4 hours', minutes: 240 },
  { label: 'Today', minutes: null },
  { label: "Don't clear", minutes: -1 },
]

function parseSettingsRoot(raw: unknown): Record<string, unknown> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return {}
    }
  }
  return raw as Record<string, unknown>
}

/**
 * Re-derive emoji/label from the backing preset so statuses saved under an old
 * label (e.g. "Vacationing", "Out sick") display with the current copy ("Out of
 * office", "Unwell") without orphaning the stored value. AHP/custom statuses are
 * left untouched.
 */
export function migrateStoredActivityStatus(
  status: ProviderActivityStatus,
): ProviderActivityStatus {
  if (status.source !== 'manual') return status
  const preset = MANUAL_PRESET_BY_ID.get(status.presetId)
  if (!preset) return status
  if (status.label === preset.label && status.emoji === preset.emoji) return status
  return { ...status, emoji: preset.emoji, label: preset.label }
}

export function getProviderActivityStatus(
  user: { settingsJson?: unknown } | null | undefined,
): ProviderActivityStatus | null {
  if (!user) return null
  const root = parseSettingsRoot(user.settingsJson)
  const status = root.activityStatus as ProviderActivityStatus | undefined
  if (!status?.label) return null
  if (status.expiresAt && Date.parse(status.expiresAt) <= Date.now()) return null
  return migrateStoredActivityStatus(status)
}

/**
 * Read the stored activity status WITHOUT applying expiry filtering. Unlike
 * {@link getProviderActivityStatus} (which returns `null` once the status has
 * expired) this returns the raw saved record so callers can inspect
 * `expiresAt` to schedule the auto-revert-to-Active timer.
 */
export function getStoredActivityStatus(
  user: { settingsJson?: unknown } | null | undefined,
): ProviderActivityStatus | null {
  if (!user) return null
  const root = parseSettingsRoot(user.settingsJson)
  const status = root.activityStatus as ProviderActivityStatus | undefined
  if (!status?.label) return null
  return migrateStoredActivityStatus(status)
}

export function formatActivityStatusDisplay(status: ProviderActivityStatus): string {
  return `${status.emoji} ${status.label}`.trim()
}

/** Resolve the manual preset that backs a saved status, if any (used to pre-select the editor). */
export function findPresetForStatus(
  status: ProviderActivityStatus | null | undefined,
): ActivityPreset | null {
  if (!status) return null
  return MANUAL_ACTIVITY_PRESETS.find((preset) => preset.id === status.presetId) ?? null
}

/**
 * Stable identity for a status, used to reconcile an optimistic self-view with the
 * authoritative value once the (eventually-consistent) profile re-fetch catches up.
 */
export function activityStatusKey(status: ProviderActivityStatus | null | undefined): string {
  if (!status) return 'none'
  return `${status.presetId ?? status.label}|${status.updatedAt ?? ''}`
}

export function computeExpiryIso(
  kind: ActivityStatusExpiryKind,
  durationMinutes: number | null,
  endDateTime?: string,
): string | null {
  if (kind === 'never') return null
  if (kind === 'datetime' && endDateTime) return new Date(endDateTime).toISOString()
  if (kind === 'duration' && durationMinutes != null && durationMinutes > 0) {
    return new Date(Date.now() + durationMinutes * 60_000).toISOString()
  }
  if (durationMinutes === null) {
    const end = new Date()
    end.setHours(23, 59, 59, 999)
    return end.toISOString()
  }
  return null
}

export function buildManualActivityStatus(input: {
  preset: ActivityPreset
  expiryKind: ActivityStatusExpiryKind
  durationMinutes: number | null
  endDateTime?: string
  pauseNotifications?: boolean
}): ProviderActivityStatus {
  const expiresAt = computeExpiryIso(input.expiryKind, input.durationMinutes, input.endDateTime)
  return {
    source: 'manual',
    presetId: input.preset.id,
    emoji: input.preset.emoji,
    label: input.preset.label,
    expiresAt,
    expiryKind: input.expiryKind,
    pauseNotifications: input.pauseNotifications ?? false,
    updatedAt: new Date().toISOString(),
  }
}

/**
 * Build the activity status that backs a posted Leave update. Sick leave sets
 * "Unwell"; every other leave type sets "Out of office" (see
 * {@link leavePresetForType}). Expires at the leave's finish datetime when
 * known; otherwise never (the provider clears it on return). Scheduled/future
 * start isn't supported by the status model today, so the status begins
 * immediately.
 */
export function buildLeaveActivityStatus(input: {
  finishAt?: string | null
  leaveType?: string | null
}): ProviderActivityStatus {
  const hasFinish = Boolean(input.finishAt && input.finishAt.trim())
  return buildManualActivityStatus({
    preset: leavePresetForType(input.leaveType),
    expiryKind: hasFinish ? 'datetime' : 'never',
    durationMinutes: null,
    endDateTime: hasFinish ? input.finishAt! : undefined,
  })
}
